import { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, InputBase, IconButton, Tooltip, Paper,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Snackbar, Alert, CircularProgress,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { fetchJson } from './api';

const GOLD = '#d4a550';
const GOLD_LIGHT = 'rgba(212,165,80,0.12)';
const GOLD_BORDER = 'rgba(212,165,80,0.3)';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function SupervisorAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchJson(`/supervisor/alerts?q=${encodeURIComponent(q)}&limit=50`);
      setAlerts(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      showSnack('Errore caricamento alert', 'error');
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, []);

  const handleEdit = (a) => {
    setEditItem({ ...a });
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetchJson(`/supervisor/alerts/${editItem.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: editItem.title,
          ruolo: editItem.ruolo,
          description: editItem.description,

          date_from: editItem.date_from || null,
          date_to: editItem.date_to || null,
        }),
      });
      setAlerts(prev => prev.map(a => a.id === editItem.id ? { ...a, ...editItem } : a));
      setEditOpen(false);
      showSnack('Alert aggiornato');
    } catch (e) {
      showSnack('Errore salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo alert?')) return;
    try {
      await fetchJson(`/supervisor/alerts/${id}`, { method: 'DELETE' });
      setAlerts(prev => prev.filter(a => a.id !== id));
      showSnack('Alert eliminato');
    } catch (e) {
      showSnack('Errore eliminazione', 'error');
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'text.secondary', mb: 0.5 }}>
          Supervisor
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#e8e6df', letterSpacing: -0.3 }}>
            Gestione Alert
          </Typography>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 2, py: 0.75,
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '10px',
            background: 'rgba(255,255,255,0.03)',
          }}>
            <SearchRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <InputBase
              placeholder="Cerca alert..."
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && load()}
              sx={{ fontSize: 13, color: '#e8e6df', width: 220 }}
            />
            <Button size="small" onClick={load} sx={{ color: GOLD, fontSize: 12, minWidth: 0, px: 1 }}>Cerca</Button>
          </Box>
        </Box>
      </Box>

      {/* Stats bar */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ px: 2, py: 1, borderRadius: '8px', background: GOLD_LIGHT, border: `1px solid ${GOLD_BORDER}` }}>
          <Typography sx={{ fontSize: 11, color: GOLD, fontWeight: 700, letterSpacing: '0.06em' }}>
            {alerts.length} ALERT TOTALI
          </Typography>
        </Box>
      </Box>

      {/* Lista alert */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: GOLD }} size={28} />
        </Box>
      ) : alerts.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '12px' }}>
          <Typography sx={{ fontSize: 14, color: 'text.disabled' }}>Nessun alert trovato.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {alerts.map(a => (
            <Paper key={a.id} sx={{
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.07)',
              background: 'rgba(255,255,255,0.02)',
              transition: 'border-color .15s',
              '&:hover': { borderColor: GOLD_BORDER },
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                {/* Info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75, flexWrap: 'wrap' }}>
                    <Box sx={{
                      px: 1, py: 0.2, borderRadius: '5px',
                      background: GOLD_LIGHT, border: `1px solid ${GOLD_BORDER}`,
                      color: GOLD, fontSize: 10, fontWeight: 700, letterSpacing: '0.07em',
                    }}>
                      {a.category || a.ruolo || 'ALERT'}
                    </Box>
                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>#{a.id}</Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>· {formatDate(a.created_at)}</Typography>
                    {a.created_by_email && (
                      <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>· {a.created_by_email}</Typography>
                    )}
                  </Box>

                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#e8e6df', mb: 0.5 }}>
                    {(() => {
                      if (a.ruolo && a.ruolo !== a.category && a.ruolo !== 'POSTPRODUZIONE') return a.ruolo;
                      const desc = a.description || '';
                      const m = desc.match(/(?:ruolo\s*(?:ricercato)?|posizione)\s*:\s*([^\n]+)/i);
                      if (m) return m[1].trim();
                      const lines = desc.split('\n').map(l => l.trim()).filter(l => l.length > 3 && !/^[A-Z\s📢🔔!·]+$/.test(l));
                      if (lines.length > 0) return lines[0].length > 80 ? lines[0].slice(0, 80) + '…' : lines[0];
                      return a.title;
                    })()}
                  </Typography>

                  {a.description && (
                    <Typography sx={{
                      fontSize: 12, color: 'text.secondary', lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                      display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {a.description}
                    </Typography>
                  )}

                  {(a.date_from || a.date_to) && (
                    <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.75 }}>
                      📅 {formatDate(a.date_from)} → {formatDate(a.date_to)}
                    </Typography>
                  )}
                </Box>

                {/* Azioni */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <Tooltip title="Modifica" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(a)}
                      sx={{ color: 'text.secondary', '&:hover': { color: GOLD, background: GOLD_LIGHT } }}
                    >
                      <EditRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Elimina" arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(a.id)}
                      sx={{ color: 'text.secondary', '&:hover': { color: '#f87171', background: 'rgba(248,113,113,0.08)' } }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Dialog modifica */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, fontSize: 18 }}>Modifica alert</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: '16px !important' }}>
          <TextField
            label="Titolo"
            fullWidth
            value={editItem?.title || ''}
            onChange={e => setEditItem(f => ({ ...f, title: e.target.value }))}
          />
          <TextField
            label="Ruolo ricercato"
            fullWidth
            value={editItem?.ruolo || ''}
            onChange={e => setEditItem(f => ({ ...f, ruolo: e.target.value }))}
          />
          <TextField
            label="Descrizione"
            fullWidth
            multiline
            rows={6}
            value={editItem?.description || ''}
            onChange={e => setEditItem(f => ({ ...f, description: e.target.value }))}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Data inizio"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editItem?.date_from ? editItem.date_from.slice(0,10) : ''}
              onChange={e => setEditItem(f => ({ ...f, date_from: e.target.value }))}
            />
            <TextField
              label="Data fine"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editItem?.date_to ? editItem.date_to.slice(0,10) : ''}
              onChange={e => setEditItem(f => ({ ...f, date_to: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Annulla
          </Button>
          <Button
            variant="contained"
            disabled={saving || !editItem?.title?.trim()}
            onClick={handleSave}
            sx={{
              background: `linear-gradient(135deg, ${GOLD}, #b8892f)`,
              color: '#0a0a0f', fontWeight: 800, textTransform: 'none',
              borderRadius: '8px', px: 3,
              '&:hover': { background: `linear-gradient(135deg, #e0b660, #c49435)` },
            }}
          >
            {saving ? 'Salvataggio…' : 'Salva modifiche'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open} autoHideDuration={2500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
